/**
 * Storage Utilities
 *
 * Centralized utilities for localStorage operations, including bookmark management.
 * This eliminates duplication of storage functions across components.
 *
 * @module storage
 */

import { logger } from './logger';

/**
 * Storage keys used throughout the application
 */
export const STORAGE_KEYS = {
  GRAMMAR_BOOKMARKS: 'learning-bookmarks',
  SENTENCE_BOOKMARKS: 'learning-sentence-bookmarks',
} as const;

/**
 * Generic function to load a Set of strings from localStorage
 *
 * @param key - The localStorage key to read from
 * @returns A Set containing the stored strings, or an empty Set if not found or on error
 */
export function loadStringSet(key: string): Set<string> {
  try {
    const stored = localStorage.getItem(key);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
}

/**
 * Generic function to save a Set of strings to localStorage
 *
 * @param key - The localStorage key to write to
 * @param value - The Set of strings to save
 */
export function saveStringSet(key: string, value: Set<string>): void {
  try {
    localStorage.setItem(key, JSON.stringify(Array.from(value)));
  } catch (error) {
    logger.error(`Failed to save to localStorage key "${key}"`, error);
  }
}

/**
 * Load grammar bookmarks from localStorage
 *
 * @returns A Set containing bookmarked topic IDs
 */
export function loadGrammarBookmarks(): Set<string> {
  return loadStringSet(STORAGE_KEYS.GRAMMAR_BOOKMARKS);
}

/**
 * Save grammar bookmarks to localStorage
 *
 * @param bookmarks - The Set of bookmarked topic IDs to save
 */
export function saveGrammarBookmarks(bookmarks: Set<string>): void {
  saveStringSet(STORAGE_KEYS.GRAMMAR_BOOKMARKS, bookmarks);
}

/**
 * Load sentence bookmarks from localStorage
 *
 * @returns A Set containing bookmarked phrase IDs
 */
export function loadSentenceBookmarks(): Set<string> {
  return loadStringSet(STORAGE_KEYS.SENTENCE_BOOKMARKS);
}

/**
 * Save sentence bookmarks to localStorage
 *
 * @param bookmarks - The Set of bookmarked phrase IDs to save
 */
export function saveSentenceBookmarks(bookmarks: Set<string>): void {
  saveStringSet(STORAGE_KEYS.SENTENCE_BOOKMARKS, bookmarks);
}

